import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["component"],
  _excluded2 = ["component"];
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useRef } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
import { IconButton } from '@atlaskit/button/new';
import ArrowLeft from '@atlaskit/icon/core/migration/arrow-left';
import ArrowLeftOld from '@atlaskit/icon/glyph/arrow-left';
import { ExitingPersistence, SlideIn, useExitingPersistence } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import { animationTimingFunction, transitionDurationMs } from '../../constants';
import { createExtender } from '../utils';
import ContentOverrides from './content';
import DrawerWrapper from './drawer-wrapper';
import FocusLock from './focus-lock';
import SidebarOverrides from './sidebar';

// Misc.
// ------------------------------

var defaults = {
  Sidebar: SidebarOverrides,
  Content: ContentOverrides
};

/**
 * This wrapper is used to specify separate durations for enter and exit.
 */
var CustomSlideIn = function CustomSlideIn(_ref) {
  var children = _ref.children,
    onFinish = _ref.onFinish,
    enterFrom = _ref.enterFrom;
  var _useExitingPersistenc = useExitingPersistence(),
    isExiting = _useExitingPersistenc.isExiting;

  /**
   * The actual duration should be the same for both enter and exit,
   * but motion halves the passed duration for exit animations,
   * so we double it when exiting.
   */
  var duration = isExiting ? transitionDurationMs * 2 : transitionDurationMs;
  return jsx(SlideIn, {
    animationTimingFunction: animationTimingFunction,
    duration: duration,
    enterFrom: enterFrom,
    exitTo: enterFrom,
    fade: "none",
    onFinish: onFinish
  }, children);
};
var DrawerPrimitive = function DrawerPrimitive(_ref2) {
  var children = _ref2.children,
    Icon = _ref2.icon,
    _ref2$closeLabel = _ref2.closeLabel,
    closeLabel = _ref2$closeLabel === void 0 ? 'Close drawer' : _ref2$closeLabel,
    scrollContentLabel = _ref2.scrollContentLabel,
    onClose = _ref2.onClose,
    onCloseComplete = _ref2.onCloseComplete,
    onOpenComplete = _ref2.onOpenComplete,
    overrides = _ref2.overrides,
    testId = _ref2.testId,
    isOpen = _ref2.in,
    shouldReturnFocus = _ref2.shouldReturnFocus,
    autoFocusFirstElem = _ref2.autoFocusFirstElem,
    isFocusLockEnabled = _ref2.isFocusLockEnabled,
    width = _ref2.width,
    label = _ref2.label,
    titleId = _ref2.titleId,
    _ref2$enterFrom = _ref2.enterFrom,
    enterFrom = _ref2$enterFrom === void 0 ? 'left' : _ref2$enterFrom;
  var getOverrides = createExtender(defaults, overrides);
  var _getOverrides = getOverrides('Sidebar'),
    Sidebar = _getOverrides.component,
    sideBarOverrides = _objectWithoutProperties(_getOverrides, _excluded);
  var _getOverrides2 = getOverrides('Content'),
    Content = _getOverrides2.component,
    contentOverrides = _objectWithoutProperties(_getOverrides2, _excluded2);

  /**
   * A ref to point to our wrapper, passed to `onCloseComplete` and `onOpenComplete` callbacks.
   */
  var drawerRef = useRef(null);
  var onFinish = useCallback(function (state) {
    if (state === 'entering') {
      onOpenComplete === null || onOpenComplete === void 0 || onOpenComplete(drawerRef.current);
    } else if (state === 'exiting') {
      onCloseComplete === null || onCloseComplete === void 0 || onCloseComplete(drawerRef.current);
    }
  }, [onCloseComplete, onOpenComplete]);
  return jsx(ExitingPersistence, {
    appear: true
  }, isOpen && jsx(CustomSlideIn, {
    onFinish: onFinish,
    enterFrom: enterFrom
  }, function (_ref3) {
    var className = _ref3.className;
    return jsx(FocusLock, {
      autoFocusFirstElem: autoFocusFirstElem,
      isFocusLockEnabled: isFocusLockEnabled,
      shouldReturnFocus: shouldReturnFocus
    }, jsx(DrawerWrapper
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    , {
      className: className,
      width: width,
      testId: testId,
      drawerRef: drawerRef,
      label: label,
      titleId: titleId
    }, jsx(Sidebar, sideBarOverrides, jsx(IconButton, {
      onClick: onClose,
      testId: testId && 'DrawerPrimitiveSidebarCloseButton',
      icon: Icon ? function (iconProps) {
        return jsx(Icon, _extends({}, iconProps, {
          size: "large"
        }, fg('platform-visual-refresh-icon-ads-migration') ? {
          LEGACY_size: 'large'
        } : {}));
      } : fg('platform-visual-refresh-icon-ads-migration') ? ArrowLeft :
      // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
      ArrowLeftOld,
      label: closeLabel,
      shape: "circle",
      appearance: "subtle"
    })), jsx(Content, _extends({
      scrollContentLabel: scrollContentLabel
    }, contentOverrides), children)));
  }));
};

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export default DrawerPrimitive;